import { useEffect, useMemo, useState } from 'react'

import { IAppLayer } from '@/core/app-builder/app/layer'
import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useAppPagesContext } from '@/context/AppPagesContext'
import { ComponentSettingKey } from '@/shared/builder'
import { collectTableIds } from '@/shared/collect-table-ids'
import { EVENT_KEYS } from '@/shared/custom-event'

// !these keys is an exception, as it should be loaded on action
const excludedKeys: string[] = ['-action-', ComponentSettingKey.FormDataSource, ComponentSettingKey.FormActionSubmit]

/**
 * This hook is used to load table ids from the layer (page or overlay) settings.
 * It will recursively find all table ids in the layer settings and add them to the set.
 * Then it will load the table data for each table id.
 *
 * @param layer - The layer (page or overlay) to load table ids from
 */
export function useLoadTableIds(layer: IAppLayer | undefined) {
  const { dbClient, dbWorker, language, loadedTables, tablesLoadingMap } = useDbClientContext()
  const { activePage } = useAppPagesContext()
  const { activeLayer } = useActiveLayerContext()

  const activePageID = activePage?.id
  const activeLayerID = activeLayer?.id

  const [pageTableIds, setPageTableIds] = useState<{ tableId: string; tableName: string; entryKeys: string[] }[]>([])

  // console.log(`pageTableIds ->`, pageTableIds)

  const isLoadingTable = useMemo(() => {
    return pageTableIds.some(({ tableId }) => tablesLoadingMap.has(tableId))
  }, [pageTableIds, tablesLoadingMap])

  // handle loading state
  useEffect(() => {
    if (isLoadingTable) {
      window.dispatchEvent(new CustomEvent(EVENT_KEYS.GLOBAL_APP_LOADING, { detail: { loading: true } }))
    } else {
      window.dispatchEvent(new CustomEvent(EVENT_KEYS.GLOBAL_APP_LOADING, { detail: { loading: false } }))
    }
  }, [isLoadingTable])

  // handle load table ids
  useEffect(() => {
    if (!dbClient || !dbWorker || !layer) return

    let timeout: NodeJS.Timeout | undefined

    const handleLoadTableIds = (layerUpdate: boolean = false) => {
      if (timeout) {
        clearTimeout(timeout)
      }
      timeout = setTimeout(() => {
        // collect table ids from layer settings
        const tableIdsMap = collectTableIds(layer.settings, dbClient, excludedKeys, 'Layer')

        if (layer.settings._TableID) {
          tableIdsMap.inject(layer.settings._TableID, 'Layer.TableID')
        }

        if (layer.settings._TableID) {
          if (tableIdsMap.has(layer.settings._TableID)) {
            tableIdsMap.get(layer.settings._TableID)?.add('Layer.TableID')
          } else {
            tableIdsMap.set(layer.settings._TableID, new Set(['Layer.TableID']))
          }
        }

        for (const tableId of tableIdsMap.keys()) {
          if (loadedTables.has(tableId) && !layerUpdate) continue
          dbWorker.sendAction({
            type: 'get_table_data',
            payload: { table_id: tableId, language },
            meta: { from: 'useLoadTableIds' },
          })
        }

        setPageTableIds(
          Array.from(
            tableIdsMap.entries().map(([tableId, keys]) => ({
              tableId,
              tableName: dbClient.tablesMap.get(tableId)?.settings._Name || '',
              entryKeys: Array.from(keys),
            }))
          )
        )
      }, 0)
    }

    // initial load
    handleLoadTableIds()

    // listen to db-table-add-row event, reload the current tableId
    const handleUpdateLayer = (e: Event) => {
      const layerID = layer.settings._id
      const tableID = layer.settings._TableID
      const isActiveLayer = layerID === activeLayerID
      const isActivePage = layerID === activePageID

      if (layerID && tableID && (isActiveLayer || isActivePage)) {
        const tableName = dbClient.tablesMap.get(tableID)?.settings._Name
        console.log('Update current active page: ->', [layer.settings._Settings._Title, tableName])
        handleLoadTableIds(true)
      }
    }

    window.addEventListener(EVENT_KEYS.DB_TABLE_ROWS_UPDATED, handleUpdateLayer)

    return () => {
      window.removeEventListener(EVENT_KEYS.DB_TABLE_ROWS_UPDATED, handleUpdateLayer)

      if (timeout) {
        clearTimeout(timeout)
      }
    }
  }, [activeLayerID, activePageID, dbClient, dbWorker, language, layer, loadedTables])

  // validate non-existing body settings
  useEffect(() => {
    if (!dbClient || !dbWorker || !layer) return

    let timeout: NodeJS.Timeout | undefined

    const handleValidateNonExistingBodySettings = () => {
      if (timeout) {
        clearTimeout(timeout)
      }
      timeout = setTimeout(() => {
        const nonExistingSettingKeys = new Set<string>()

        for (const settingKey of Object.keys(layer.settings._BodySettings || {})) {
          // check if the setting key exist in its component map
          if (!layer.componentsMap.has(settingKey)) {
            nonExistingSettingKeys.add(settingKey)
          }
        }

        if (nonExistingSettingKeys.size > 0) {
          console.log('nonExistingSettingKeys ->', {
            nonExistingSettingKeys,
            layer,
          })
        }
      }, 0)
    }

    handleValidateNonExistingBodySettings()

    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }
  }, [dbClient, dbWorker, layer])
}
